import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Check, ChevronRight, Circle } from "lucide-react"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/useMediaQuery"
import {
  SheetRoot,
  SheetTrigger,
  SheetShell,
  SheetTitle,
  SHEET_ROW_CLASS,
} from "@/components/mobile/SheetShell"

/**
 * Responsive menu.
 *
 * Desktop: the classic anchored Radix dropdown.
 * Phones: the same components render a native-style *action sheet* sliding up
 * from the bottom (big 52px rows, safe-area aware, swipe to dismiss). Nested
 * <DropdownMenuSub> menus become inline accordions.
 */
const MenuSheetContext = React.createContext(null)
const SubContext = React.createContext(null)

function MobileMenuRoot({ children, open, defaultOpen, onOpenChange }) {
  const [internal, setInternal] = React.useState(!!defaultOpen)
  const controlled = open !== undefined
  const current = controlled ? open : internal
  const setOpen = React.useCallback(
    (v) => {
      if (!controlled) setInternal(v)
      onOpenChange?.(v)
    },
    [controlled, onOpenChange]
  )
  const ctx = React.useMemo(() => ({ open: current, setOpen }), [current, setOpen])
  return (
    <MenuSheetContext.Provider value={ctx}>
      <SheetRoot open={current} onOpenChange={setOpen}>
        {/* The sheet is portaled, but React still bubbles its clicks (and the dimmed overlay's)
            through the component tree: keep them from reaching clickable ancestors such as a card. */}
        <div style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      </SheetRoot>
    </MenuSheetContext.Provider>
  )
}

const DropdownMenu = ({ children, ...props }) => {
  const isMobile = useIsMobile()
  if (isMobile) return <MobileMenuRoot {...props}>{children}</MobileMenuRoot>
  return <DropdownMenuPrimitive.Root {...props}>{children}</DropdownMenuPrimitive.Root>
}

const DropdownMenuTrigger = React.forwardRef((props, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  const Comp = sheet ? SheetTrigger : DropdownMenuPrimitive.Trigger
  return <Comp ref={ref} {...props} />
})
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

const DropdownMenuGroup = React.forwardRef((props, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  if (sheet) return <div ref={ref} role="group" {...props} />
  return <DropdownMenuPrimitive.Group ref={ref} {...props} />
})
DropdownMenuGroup.displayName = "DropdownMenuGroup"

const DropdownMenuPortal = ({ children, ...props }) => {
  const sheet = React.useContext(MenuSheetContext)
  // Inside a sheet, sub-menus render inline: there is nothing to portal.
  if (sheet) return <>{children}</>
  return <DropdownMenuPrimitive.Portal {...props}>{children}</DropdownMenuPrimitive.Portal>
}

const DropdownMenuSub = ({ children, open, defaultOpen, onOpenChange, ...props }) => {
  const sheet = React.useContext(MenuSheetContext)
  const [expanded, setExpanded] = React.useState(!!defaultOpen)
  if (sheet) {
    return (
      <SubContext.Provider value={{ expanded, toggle: () => setExpanded((v) => !v) }}>
        {children}
      </SubContext.Provider>
    )
  }
  return (
    <DropdownMenuPrimitive.Sub open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} {...props}>
      {children}
    </DropdownMenuPrimitive.Sub>
  )
}

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

// Row look shared by every interactive item of the action sheet.
const SHEET_ROW = SHEET_ROW_CLASS

const DropdownMenuSubTrigger = React.forwardRef(({ className, inset, children, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  const sub = React.useContext(SubContext)
  if (sheet) {
    return (
      <button
        ref={ref}
        type="button"
        role="menuitem"
        aria-expanded={!!sub?.expanded}
        onClick={() => sub?.toggle()}
        className={cn(SHEET_ROW, className)}
        {...props}>
        {children}
        <ChevronRight
          className={cn("ml-auto !mr-0 !size-4 !text-neutral-400 transition-transform", sub?.expanded && "rotate-90")} />
      </button>
    )
  }
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(
        "flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        inset && "pl-8",
        className
      )}
      {...props}>
      {children}
      <ChevronRight className="ml-auto" />
    </DropdownMenuPrimitive.SubTrigger>
  )
})
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  const sub = React.useContext(SubContext)
  if (sheet) {
    if (!sub?.expanded) return null
    return (
      <div ref={ref} role="menu" className="mb-1 ml-3 border-l-2 border-stone-200 pl-1">
        {children}
      </div>
    )
  }
  return (
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
        className
      )}
      {...props}>
      {children}
    </DropdownMenuPrimitive.SubContent>
  )
})
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

// `title` only exists for the phone action sheet (what the actions apply to); a desktop
// dropdown has no header, so it is not forwarded to the DOM there.
const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, children, title, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  if (sheet) {
    // Radix-only props (align, side, …) are meaningless for a bottom sheet.
    const { "data-testid": testId } = props
    return (
      <SheetShell
        ref={ref}
        data-testid={testId}
        showClose={false}
        bodyClassName="px-2 pt-0">
        {title ? (
          <SheetTitle className="truncate px-3 pb-1 pt-1 text-[13px] font-semibold text-neutral-500">{title}</SheetTitle>
        ) : (
          <SheetTitle className="sr-only">Menu</SheetTitle>
        )}
        <div role="menu" className="pb-1">
          {children}
        </div>
      </SheetShell>
    )
  }
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
          className
        )}
        {...props}>
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  )
})
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef(({ className, inset, onClick, onSelect, disabled, children, asChild, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  if (sheet) {
    const handle = (e) => {
      onClick?.(e)
      onSelect?.(e)
      // Radix semantics: a handler can keep the menu open with preventDefault().
      if (!e.defaultPrevented) sheet.setOpen(false)
    }
    return (
      <button
        ref={ref}
        type="button"
        role="menuitem"
        disabled={disabled}
        onClick={handle}
        className={cn(SHEET_ROW, className)}
        {...props}>
        {children}
      </button>
    )
  }
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      asChild={asChild}
      disabled={disabled}
      onClick={onClick}
      onSelect={onSelect}
      className={cn(
        "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0",
        inset && "pl-8",
        className
      )}
      {...props}>
      {children}
    </DropdownMenuPrimitive.Item>
  )
})
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, checked, onCheckedChange, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  if (sheet) {
    return (
      <button
        ref={ref}
        type="button"
        role="menuitemcheckbox"
        aria-checked={!!checked}
        onClick={() => onCheckedChange?.(!checked)}
        className={cn(SHEET_ROW, className)}
        {...props}>
        <span className="mr-3 grid size-5 place-items-center">{checked && <Check className="size-5" />}</span>
        {children}
      </button>
    )
  }
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      checked={checked}
      onCheckedChange={onCheckedChange}
      {...props}>
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
})
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef(({ className, children, value, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  if (sheet) {
    // Radio groups aren't used on phones yet: render as a plain selectable row.
    return (
      <button ref={ref} type="button" role="menuitemradio" className={cn(SHEET_ROW, className)} {...props}>
        <span className="mr-3 grid size-5 place-items-center"><Circle className="size-2 fill-current" /></span>
        {children}
      </button>
    )
  }
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      value={value}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}>
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Circle className="h-2 w-2 fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
})
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef(({ className, inset, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  if (sheet) {
    return (
      <div
        ref={ref}
        className={cn("px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-400", className)}
        {...props} />
    )
  }
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className)}
      {...props} />
  )
})
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => {
  const sheet = React.useContext(MenuSheetContext)
  if (sheet) {
    return <div ref={ref} role="separator" className={cn("mx-3 my-1 h-px bg-stone-100", className)} {...props} />
  }
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-muted", className)}
      {...props} />
  )
})
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      {...props} />
  );
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
